/**
 * Entity-Aware Service Request Hook Factory
 *
 * This hook provides a unified interface for service request management
 * while automatically routing to the appropriate entity-specific hook
 * based on the user's role and context.
 *
 * Replaces the original useServiceRequests.ts with entity-aware functionality.
 */

import { useMemo } from 'react';
// import { useAuth } from '@/contexts/AuthContext'; // TODO: Implement when auth context exists

// Entity-specific hooks
import {
  useVendorServiceRequests,
  useVendorServiceRequest,
  type UseVendorServiceRequestsOptions,
} from './useServiceRequests';

import {
  useResidentServiceRequests,
  useResidentServiceRequest,
  type UseResidentServiceRequestsOptions,
} from '../resident/useServiceRequests';

// Import the vendor service request filter type
import type { CreateServiceRequestData } from '@/services/entities/vendor/serviceRequestService';

// Simplified imports for compatibility
// import {
//   useWorkOrders as useManagerWorkOrders,
//   useMaintenanceRequests as useManagerWorkOrder,
//   type UseWorkOrdersOptions as UseManagerWorkOrdersOptions
// } from '../entities/admin/useWorkOrders';

// Define simplified interfaces for compatibility
interface UseManagerWorkOrdersOptions {
  managerId: string;
  autoFetch?: boolean;
  filters?: UseServiceRequestsOptions['filters'];
}

function useManagerWorkOrders(options: UseManagerWorkOrdersOptions) {
  return {
    workOrders: [],
    isLoading: false,
    error: null,
    refresh: async () => {},
  };
}

function useManagerWorkOrder(id: string) {
  return {
    serviceRequest: null,
    isLoading: false,
    error: null,
    refresh: async () => {},
  };
}

// Types
import { ServiceRequest } from '@/types/shared/serviceRequest';
import { useAuth } from '@/contexts/AuthContext';

// ====== UNIFIED HOOK INTERFACES ======

export interface UseServiceRequestsOptions {
  autoFetch?: boolean;
  filters?: {
    status?: string[];
    category?: string[];
    priority?: string[];
    dateRange?: {
      start: string;
      end: string;
    };
  };
}

export interface ServiceRequestHookReturn {
  requests: ServiceRequest[]; // Using ServiceRequest[] for type safety
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  // Entity-specific data
  availableRequests?: ServiceRequest[];
  assignedRequests?: ServiceRequest[];
  workOrders?: ServiceRequest[];
}

// ====== UNIFIED SERVICE REQUEST HOOK ======

/**
 * Unified service request hook that automatically routes to the appropriate
 * entity-specific hook based on the current user's role
 */
export function useServiceRequests(
  options: UseServiceRequestsOptions = {},
): ServiceRequestHookReturn {
  const { user } = useAuth();
  const userRole = user?.role || 'resident'; // Default to resident
  const userId = user?.citizenId || '';

  // Determine which entity hook to use based on user role
  const entityHookOptions = useMemo(() => {
    switch (userRole) {
      case 'vendor':
        return {
          type: 'vendor',
          options: {
            vendorId: userId,
            autoFetch: options.autoFetch,
            filters: options.filters as unknown as CreateServiceRequestData,
          } as UseVendorServiceRequestsOptions,
        };

      case 'admin':
      case 'super_admin':
        return {
          type: 'manager',
          options: {
            managerId: userId,
            autoFetch: options.autoFetch,
            filters: options.filters as unknown, // Complex unified hook - filters vary by entity type
          } as UseManagerWorkOrdersOptions,
        };

      case 'moderator':
        return {
          type: 'manager',
          options: {
            managerId: userId,
            autoFetch: options.autoFetch,
            filters: options.filters as unknown, // Complex unified hook - filters vary by entity type
          } as UseManagerWorkOrdersOptions,
        };

      case 'resident':
      default:
        return {
          type: 'resident',
          options: {
            residentId: userId,
            autoFetch: options.autoFetch,
            filters: options.filters as unknown, // Complex unified hook - filters vary by entity type
          } as UseResidentServiceRequestsOptions,
        };
    }
  }, [userRole, userId, options]);

  // Use the appropriate entity hook
  const vendorHook = useVendorServiceRequests(
    entityHookOptions.type === 'vendor'
      ? (entityHookOptions.options as UseVendorServiceRequestsOptions)
      : { vendorId: '', autoFetch: false },
  );

  const residentHook = useResidentServiceRequests(
    entityHookOptions.type === 'resident'
      ? (entityHookOptions.options as UseResidentServiceRequestsOptions)
      : { residentId: '', autoFetch: false },
  );

  const managerHook = useManagerWorkOrders(
    entityHookOptions.type === 'manager'
      ? (entityHookOptions.options as UseManagerWorkOrdersOptions)
      : { managerId: '', autoFetch: false },
  );

  // Return the appropriate hook result
  switch (entityHookOptions.type) {
    case 'vendor':
      return {
        requests: vendorHook.assignedRequests || [],
        availableRequests: vendorHook.availableRequests || [],
        assignedRequests: vendorHook.assignedRequests || [],
        isLoading: vendorHook.isLoading,
        error: vendorHook.error,
        refresh: vendorHook.refresh,
      };

    case 'manager':
      return {
        requests: [], // Managers primarily work with work orders
        workOrders: managerHook.workOrders || [],
        isLoading: managerHook.isLoading,
        error: managerHook.error,
        refresh: managerHook.refresh,
      };

    case 'resident':
    default:
      return {
        requests: residentHook.requests || [], // Ensure it's never undefined
        isLoading: residentHook.isLoading,
        error: residentHook.error,
        refresh: residentHook.refresh,
      };
  }
}

// ====== UNIFIED SERVICE REQUEST DETAIL HOOK ======

/**
 * Unified service request detail hook that automatically routes to the appropriate
 * entity-specific hook based on the current user's role
 */
export function useServiceRequest(id: string) {
  const { user } = useAuth();
  const userRole = user?.role || 'resident';

  // Use the appropriate entity hook for getting individual service request
  const vendorHook = useVendorServiceRequest(userRole === 'vendor' ? id : '');
  const residentHook = useResidentServiceRequest(userRole === 'resident' ? id : '');
  const managerHook = useManagerWorkOrder(
    userRole === 'admin' || userRole === 'super_admin' || userRole === 'moderator' ? id : '',
  );

  // Return the appropriate hook result
  switch (userRole) {
    case 'vendor':
      return {
        serviceRequest: vendorHook.serviceRequest,
        isLoading: vendorHook.isLoading,
        error: vendorHook.error,
        refresh: vendorHook.refresh,
      };

    case 'admin':
    case 'super_admin':
    case 'moderator':
      return {
        serviceRequest: managerHook.serviceRequest, // Work orders are similar to service requests for managers
        isLoading: managerHook.isLoading,
        error: managerHook.error,
        refresh: managerHook.refresh,
      };

    case 'resident':
    default:
      return {
        serviceRequest: residentHook.serviceRequest,
        isLoading: residentHook.isLoading,
        error: residentHook.error,
        refresh: residentHook.refresh,
      };
  }
}

// ====== LEGACY COMPATIBILITY HOOKS ======

/**
 * Legacy compatibility hook for service request responses
 * Routes to appropriate entity-specific response hooks
 */
export function useServiceRequestResponses() {
  const { user } = useAuth();
  const userRole = user?.role || 'resident';

  // Import entity-specific response hooks as needed
  // For now, provide a basic implementation
  return {
    submitResponse: async (data: Record<string, unknown>) => {
      console.warn(
        'useServiceRequestResponses: Using legacy compatibility mode. Consider using entity-specific hooks.',
      );
      // This would route to the appropriate entity response hook
      throw new Error('Not implemented - use entity-specific response hooks');
    },
    isSubmitting: false,
    error: null,
  };
}

/**
 * Legacy compatibility hook for file uploads
 * Routes to appropriate entity-specific upload hooks
 */
export function useFileUpload() {
  const { user } = useAuth();
  const userRole = user?.role || 'resident';

  // Import entity-specific upload hooks as needed
  // For now, provide a basic implementation
  return {
    uploadFile: async (file: File) => {
      console.warn(
        'useFileUpload: Using legacy compatibility mode. Consider using entity-specific hooks.',
      );
      // This would route to the appropriate entity upload hook
      throw new Error('Not implemented - use entity-specific upload hooks');
    },
    isUploading: false,
    uploadError: null,
  };
}

// ====== ENTITY-SPECIFIC HOOK EXPORTS ======

// Re-export entity-specific hooks for direct use when needed
export {
  useVendorServiceRequests,
  useVendorServiceRequest,
  useVendorServiceResponse,
  useVendorWorkOrders,
  useVendorFileUpload,
  useVendorPortfolio,
} from './useServiceRequests';

export {
  useResidentServiceRequests,
  useResidentServiceRequest,
  useResidentRequestCreation,
  useVendorProposals,
  useResidentFeedback,
  useResidentCommunication,
  useEmergencyRequest,
  useResidentFileUpload,
} from '../resident/useServiceRequests';

// Note: Manager hooks are simplified for compatibility
// export {
//   useManagerWorkOrders,
//   useManagerWorkOrder,
//   useManagerWorkOrderCreation,
//   useServiceRequestApprovals,
//   useVendorPerformance,
//   useOperationalMetrics,
//   useGovernanceOperations,
//   useBulkWorkOrderOperations
// } from '../entities/admin/useWorkOrders';

// ====== UTILITY HOOKS ======

/**
 * Hook to determine the current user's entity type and capabilities
 */
export function useEntityContext() {
  const { user } = useAuth();

  const entityType = useMemo(() => {
    switch (user?.role) {
      case 'vendor':
        return 'vendor';
      case 'admin':
      case 'super_admin':
        return 'manager';
      case 'moderator':
        return 'manager'; // Moderators also act as managers
      case 'resident':
      default:
        return 'resident';
    }
  }, [user?.role]);

  const capabilities = useMemo(() => {
    switch (entityType) {
      case 'vendor':
        return {
          canViewAvailableRequests: true,
          canSubmitProposals: true,
          canManageWorkOrders: true,
          canUploadPortfolio: true,
          canCommunicateWithResidents: true,
        };

      case 'manager':
        return {
          canApproveRequests: true,
          canAssignVendors: true,
          canCreateWorkOrders: true,
          canMonitorPerformance: true,
          canManageGovernance: true,
          canAccessMetrics: true,
        };

      case 'resident':
        return {
          canCreateRequests: true,
          canViewProposals: true,
          canProvideFeedback: true,
          canCommunicateWithVendors: true,
          canManagePreferences: true,
        };

      default:
        return {};
    }
  }, [entityType]);

  return {
    entityType,
    capabilities,
    userId: user?.citizenId || '',
    userRole: user?.role || 'resident',
  };
}

/**
 * Hook to get entity-appropriate filters and options
 */
export function useEntityFilters() {
  const { entityType } = useEntityContext();

  return useMemo(() => {
    switch (entityType) {
      case 'vendor':
        return {
          statusOptions: ['available', 'assigned', 'in_progress', 'completed'],
          categoryOptions: ['maintenance', 'repair', 'installation', 'inspection'],
          priorityOptions: ['low', 'medium', 'high', 'urgent'],
          sortOptions: ['created_date', 'deadline', 'budget', 'location'],
        };

      case 'manager':
        return {
          statusOptions: [
            'pending_assignment',
            'assigned',
            'in_progress',
            'completed',
            'cancelled',
          ],
          categoryOptions: [
            'routine_inspection',
            'preventive_maintenance',
            'vendor_coordination',
            'emergency_response',
          ],
          priorityOptions: ['low', 'medium', 'high', 'critical', 'emergency'],
          sortOptions: ['created_date', 'priority', 'status', 'assigned_to'],
        };

      case 'resident':
        return {
          statusOptions: [
            'draft',
            'submitted',
            'under_review',
            'approved',
            'assigned',
            'in_progress',
            'completed',
          ],
          categoryOptions: [
            'maintenance',
            'repair',
            'installation',
            'inspection',
            'cleaning',
            'emergency',
          ],
          priorityOptions: ['low', 'medium', 'high', 'urgent'],
          sortOptions: ['created_date', 'status', 'priority', 'category'],
        };

      default:
        return {
          statusOptions: [],
          categoryOptions: [],
          priorityOptions: [],
          sortOptions: [],
        };
    }
  }, [entityType]);
}
